import { createSelector } from "@reduxjs/toolkit";
import { taxonomyApi } from "./api/productTaxonomyApi";
import { productApi } from "./api/productApi";
import { RootState } from "../../../app/store";
import { CategoryNode } from "./types/productTaxonomyApiTypes";

// Base selector to get the category tree data from the API endpoint
const selectCategoriesResult = taxonomyApi.endpoints.getCategories.select();

/**
 * Memoized selector that retrieves the pre-built category tree from the API cache.
 * All hierarchical sorting and translation logic is now handled by the backend.
 */
export const selectCategoryTree = createSelector(
  [selectCategoriesResult],
  (categoriesResult): CategoryNode[] => {
    return categoriesResult.data ?? [];
  },
);

// --- Filter Activity Selectors ---
// Input selector that gets just the filters object
const selectFilters = (state: RootState) => state.product.filters;

// The memoized selector that computes the boolean value
export const selectAreFiltersActive = createSelector(
  [selectFilters],
  (filters) =>
    filters.category.length > 0 ||
    filters.vehicle.length > 0 ||
    filters.tag.length > 0 ||
    filters.brand.length > 0 ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.search !== "" ||
    filters.onSale === true ||
    filters.inStock === true ||
    Object.keys(filters.attributes).length > 0,
);

// 1. New, stable selector to get the collection data result for the current filters.
//    This replaces the original unstable `selectCollectionDataResult` function.
const selectCollectionDataResult = createSelector(
  [
    selectFilters, // This is a memoized input selector.
    (state: RootState) => state, // Pass the whole state through.
  ],
  (filters, state) => {
    // The result function only re-runs if `filters` or `state` reference changes.
    // `filters` is stable thanks to the input selector.

    // `select` is memoized. Given the same `filters` object, it returns the same selector instance.
    const selectDataForFilters =
      productApi.endpoints.getProductsCollectionData.select(filters);

    // We execute the memoized selector instance with the current state.
    return selectDataForFilters(state);
  },
);

// 2. Memoized selector to derive the currency symbol from the query data.
//    This now uses the new, stable `selectCollectionDataResult` as its input.
export const selectCurrencySymbol = createSelector(
  [selectCollectionDataResult],
  (collectionResult) => {
    // If data exists, return its symbol. Otherwise, provide a safe fallback.
    return collectionResult.data?.currencySymbol ?? "₺";
  },
);
